import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Settings,
  LayoutDashboard,
  PanelLeftClose,
  PanelLeftOpen,
  Building2,
  Home,
  BarChart3,
  UserPlus,
  MapPin,
  DollarSign,
  UsersIcon,
  ChevronDown,
  Clipboard,
  Bell,
  Zap,
  Palette,
  Activity,
  FileText,
  FileSignature,
  Brain,
  MessageSquare,
  Plug,
  Sun,
  Network,
  Coins,
  Wallet,
  Gauge,
  CircleDollarSign,
  Banknote,
  ShieldCheck,
  Ban,
  SlidersHorizontal,
  DoorOpen,
  Puzzle,
  MapPinned,
  Radio,
  KeyRound,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useCommissionCostingEnabled } from "@/hooks/useCommissionCostingEnabled";
import { useCommissionAreaCounts, useCommissionNavViewer } from "@/hooks/useCommissionAreaCounts";
import { badgeForDestination, rolledBadgeCount } from "@/lib/commissionAreaCounts";
import {
  COMMISSION_AREA_COPY,
  canSeeCommissionGroup,
  isCommissionOverviewPath,
  isCommissionSetupPath,
  visibleCommissionDestinations,
  type CommissionDestinationId,
} from "@/lib/commissionAreaNav";
import { cn } from "@/lib/utils";
import { usePermissions } from "@/contexts/PermissionContext";
import { useOnboardingAccess } from "@/hooks/useOnboardingAccess";
import { useDesignFeatureAccess } from "@/hooks/useDesignFeatureAccess";
import { useAccountFeatures } from "@/hooks/useAccountFeatures";
import { featureVisibleTo } from "@/lib/accountFeatures";
import { useKnockMode } from "@/hooks/useKnockMode";
import { knockModeSettingsVisible } from "@/lib/knockMode";
import { doorMapVisible } from "@/lib/doorSeeding";
import { LIVE_MAP_PERMISSIONS, liveTeamMapVisible } from "@/lib/liveTeamMap";
import { KNOCK_REVIEW_PERMISSIONS, knockReviewVisible } from "@/lib/knockReview";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import { canAdministerRoles } from "@/lib/rolesAdministration";
import { ROLES_SETTINGS_HREF, settingsEntryVisible } from "@/lib/accountRoles";
import { useState, useEffect } from "react";
import {
  type SidebarNavGroupId,
  type SidebarNavGroupMap,
  isSidebarNavGroupOpen,
  loadSidebarNavGroups,
  paintedSidebarNavGroupOpen,
  writeSidebarNavGroups,
} from "@/lib/sidebarNavGroupCookie";
import { supabase } from "@/lib/supabaseClient";
import { TenantDropdown } from "@/components/layout/TenantDropdown";
import { useSubAccountUsage } from "@/hooks/useSubAccountUsage";
import { useTenant } from "@/contexts/TenantContext";
import { offersMergedScope, scopeReachKey } from "@/lib/mergedScope";
import {
  LAST_VIEWED_DASHBOARD_KEY,
  dashboardScopeKey,
  readLastViewedDashboard,
} from "@/lib/lastViewedDashboard";
import { defaultDashboardIdForScope } from "@/lib/builtinDashboards";
import { groupPipelinesByLineage, type PipelineGroup } from "@/lib/pipelineLineage";
import { fetchScopePipelines } from "@/services/pipelineScopeService";
import { filterPipelinesByAccess, hasGeneralPipelinePermission } from "@/utils/pipelinePermissions";

type NavItem = {
  title: string;
  href: string;
  icon: React.ReactNode;
  isVisible?: boolean;
  badge?: number | null;
};

type Pipeline = {
  id: number;
  name: string;
  pipeline_type: 'commercial' | 'residential' | 'onboarding';
  role_permissions?: string[];
  user_permissions?: string[];
  team_permissions?: string[];
  /** SUB-4.5 · FR40. Present on every row so the navigator can group by lineage. */
  tenant_id?: string | null;
  cloned_from_pipeline_id?: number | null;
};

/**
 * SUB-4.5 · FR40. What the navigator actually renders: one entry per workflow,
 * carrying every account's copy of it so the board behind it can build its tab
 * strip without asking again.
 */
type PipelineNavEntry = PipelineGroup<Pipeline>;

// RBAC-6.1 / FR-19: hidden in an account with Contracts switched off, like its settings entry.
const CONTRACTS_HREF = "/app/contracts";

// Admin-only navigation items
const adminNavItems: NavItem[] = [
  { title: "Reporting", href: "/app/reporting", icon: <BarChart3 className="h-5 w-5" /> },
  { title: "Contracts", href: CONTRACTS_HREF, icon: <FileText className="h-5 w-5" /> },
  { title: "Tracking", href: "/app/tracking", icon: <MapPin className="h-5 w-5" /> }
];

/**
 * Settings destinations, grouped.
 *
 * This is the whole settings navigation now — the Settings page's own tab bar is
 * gone, so every panel has to be reachable from here. Contracts in particular was
 * missing from this list while the tab bar carried it; dropping the bar without
 * adding it would have orphaned that panel behind a hand-typed URL.
 */
type SettingsGroup = { label: string; items: NavItem[] };

/** Named so the render-time gate below and the static entry cannot drift apart. */
const DESIGN_SETTINGS_HREF = "/app/settings?tab=design";
const CONTRACTS_SETTINGS_HREF = "/app/settings?tab=contracts";
const KNOCK_SETTINGS_HREF = "/app/settings?tab=knock";
const MODULES_SETTINGS_HREF = "/app/settings?tab=modules";

const settingsGroups: SettingsGroup[] = [
  {
    label: "People & Structure",
    items: [
      { title: "Users", href: "/app/settings?tab=users", icon: <UsersIcon className="h-5 w-5" /> },
      // RBAC-3.1. Gated at render on roles.administer, never manage_roles (settingsEntryVisible).
      // Its own route: /app/settings is behind SettingsGuard, which redirects without manage_settings.
      { title: "Roles & Permissions", href: ROLES_SETTINGS_HREF, icon: <KeyRound className="h-5 w-5" /> },
      { title: "Pipelines", href: "/app/settings?tab=pipelines", icon: <BarChart3 className="h-5 w-5" /> },
    ],
  },
  {
    label: "Workflow",
    items: [
      { title: "Templates", href: "/app/settings?tab=templates", icon: <Clipboard className="h-5 w-5" /> },
      { title: "Automation", href: "/app/settings?tab=webhooks", icon: <Zap className="h-5 w-5" /> },
      // RBAC-6.1 / FR-19. Gated at render: in an account with Contracts switched off, nobody but a
      // superadmin learns of it from settings, tenant admins included.
      { title: "Contracts", href: CONTRACTS_SETTINGS_HREF, icon: <FileSignature className="h-5 w-5" /> },
      // SD-8.1. Gated at render: a tenant whose design gate is closed must not learn the
      // feature exists from its own settings entry (FR-29, the SD-2.1 rule).
      { title: "Design", href: DESIGN_SETTINGS_HREF, icon: <Sun className="h-5 w-5" /> },
      { title: "Adders", href: "/app/settings?tab=adders", icon: <DollarSign className="h-5 w-5" /> },
      // KNK-1.11 / FR-11. Gated at render like Design: an account with Knock Mode off must not
      // learn of it from its own settings. Since KNK-1.55 it is switched under Add-on modules.
      { title: "Knock Mode", href: KNOCK_SETTINGS_HREF, icon: <DoorOpen className="h-5 w-5" /> },
      // KNK-1.55 / FR-11a. Superadmin only, gated at render; the database refuses everyone else.
      { title: "Add-on modules", href: MODULES_SETTINGS_HREF, icon: <Puzzle className="h-5 w-5" /> },
    ],
  },
  {
    label: "Connections",
    items: [
      { title: "Notifications", href: "/app/settings?tab=notifications", icon: <Bell className="h-5 w-5" /> },
      { title: "Integrations", href: "/app/settings?tab=integrations", icon: <Plug className="h-5 w-5" /> },
    ],
  },
  {
    label: "System",
    items: [
      // TBR-1.2 — label only; the ?tab=theme value is unchanged (see Settings.tsx SECTION_LABELS).
      { title: "Branding", href: "/app/settings?tab=theme", icon: <Palette className="h-5 w-5" /> },
      { title: "Logs", href: "/app/settings?tab=logs", icon: <Activity className="h-5 w-5" /> },
      { title: "AI", href: "/app/settings?tab=knowledge", icon: <Brain className="h-5 w-5" /> },
    ],
  },
];

const COMMISSION_DESTINATION_ICONS: Record<CommissionDestinationId, React.ReactNode> = {
  overview: <Gauge className="h-5 w-5" />,
  periods: <Wallet className="h-5 w-5" />,
  "money-held": <CircleDollarSign className="h-5 w-5" />,
  draws: <Banknote className="h-5 w-5" />,
  guarantees: <ShieldCheck className="h-5 w-5" />,
  cancelled: <Ban className="h-5 w-5" />,
  setup: <SlidersHorizontal className="h-5 w-5" />,
};

/**
 * Shared row styling for every sidebar entry.
 *
 * Hover and active both resolve to the theme's *sidebar* tokens rather than
 * `--primary`. The old treatment was `bg-primary/20 text-primary`, which goes
 * invisible whenever a theme's primary matches its sidebar color — that is
 * exactly the case on Fair Seas' "Charted Course" (both #212F44) and
 * "Deep Anchor". Hover was likewise hardcoded to `bg-gray-600`, so the
 * sidebarHoverBgColor / sidebarHoverTextColor a tenant configures never applied.
 */
const ROW_BASE =
  "group relative flex w-full items-center rounded-md font-medium transition-colors duration-150 " +
  "hover:bg-[var(--sidebar-hover-bg-color,#374151)] hover:text-[var(--sidebar-hover-text-color,#ffffff)] " +
  "focus-visible:outline-none focus-visible:ring-2 " +
  "focus-visible:ring-[var(--sidebar-hover-text-color,#ffffff)]";

const rowStyle = (active: boolean): React.CSSProperties =>
  active
    ? {
        backgroundColor: "var(--sidebar-hover-bg-color, #374151)",
        color: "var(--sidebar-hover-text-color, #ffffff)",
      }
    : { color: "var(--sidebar-text-color, #ffffff)", backgroundColor: "transparent" };

/** Hairline derived from the sidebar text color, so it works on any sidebar. */
const hairline = (pct: number) =>
  `color-mix(in srgb, var(--sidebar-text-color, #ffffff) ${pct}%, transparent)`;

/**
 * Left bar marking the active row. Uses the sidebar hover text color, which a
 * theme guarantees is legible against the sidebar, falling back to the accent.
 */
function ActiveBar() {
  return (
    <span
      aria-hidden="true"
      className="absolute left-0 top-[15%] bottom-[15%] w-[3px] rounded-r-sm"
      style={{ backgroundColor: "var(--sidebar-hover-text-color, var(--accent, #10B981))" }}
    />
  );
}

type NavRowProps = {
  title: string;
  icon: React.ReactNode;
  active?: boolean;
  collapsed?: boolean;
  /** False while a peek is opening or closing, so the words fade rather than pop. */
  labelsVisible?: boolean;
  nested?: boolean;
  to?: string;
  onClick?: () => void;
  expandable?: boolean;
  expanded?: boolean;
  /** Count badge. Hidden at 0 and while unknown — never a claim of "nothing". */
  badge?: number | null;
};

function NavRow({
  title,
  icon,
  active = false,
  collapsed = false,
  labelsVisible = true,
  nested = false,
  to,
  onClick,
  expandable = false,
  expanded = false,
  badge = null,
}: NavRowProps) {
  const className = cn(
    ROW_BASE,
    nested
      ? "px-3 py-1.5 text-xs"
      : collapsed
        ? "justify-center p-2 text-sm"
        : "px-3 py-2 text-sm",
    active && "font-semibold"
  );

  const body = (
    <>
      {active && <ActiveBar />}
      <span
        className={cn(
          "flex-shrink-0",
          nested ? "mr-2 [&>svg]:h-3 [&>svg]:w-3" : collapsed ? "" : "mr-3"
        )}
      >
        {icon}
      </span>
      {!collapsed && (
        // whitespace-nowrap rather than `truncate`: an element with overflow:hidden
        // has a min-content width of 0, so it cannot push the sidebar wider. Nowrap
        // lets the longest label set the intrinsic width, which is what makes the
        // rail size to its content up to the max-width.
        <span
          className={cn(
            "whitespace-nowrap transition-opacity duration-200",
            labelsVisible ? "opacity-100" : "opacity-0",
            expandable && "flex-1 text-left"
          )}
        >
          {title.trim()}
        </span>
      )}
      {badge != null && badge > 0 && !collapsed && (
        <span
          className={cn(
            "ml-auto rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums transition-opacity duration-200",
            labelsVisible ? "opacity-100" : "opacity-0"
          )}
          style={{
            backgroundColor: hairline(22),
            color: "var(--sidebar-text-color, #ffffff)",
          }}
        >
          {badge}
        </span>
      )}
      {expandable && !collapsed && (
        <ChevronDown
          className={cn(
            "h-4 w-4 flex-shrink-0 transition-[transform,opacity] duration-200",
            labelsVisible ? "opacity-100" : "opacity-0",
            expanded && "rotate-180"
          )}
        />
      )}
    </>
  );

  if (to) {
    return (
      <Link
        to={to}
        data-nav-link
        className={className}
        style={rowStyle(active)}
        title={title.trim()}
        aria-current={active ? "page" : undefined}
      >
        {body}
      </Link>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      data-nav-link
      className={className}
      style={rowStyle(active)}
      title={title.trim()}
      aria-expanded={expandable ? expanded : undefined}
      aria-current={active && !expandable ? "page" : undefined}
    >
      {body}
    </button>
  );
}

/** A labelled section. Collapsed rails show a hairline instead of the label. */
function NavGroup({
  label,
  collapsed,
  labelsVisible = true,
  children,
  collapsible = false,
  open = true,
  onToggle,
  badge = null,
}: {
  label: string;
  collapsed: boolean;
  labelsVisible?: boolean;
  children: React.ReactNode;
  collapsible?: boolean;
  open?: boolean;
  onToggle?: () => void;
  badge?: number | null;
}) {
  const reduceMotion = useReducedMotion();
  const slideMs = reduceMotion ? 0 : 200;

  const heading = collapsed ? (
    <div
      className="mx-2 my-2 h-px"
      style={{ backgroundColor: hairline(18) }}
      aria-hidden="true"
    />
  ) : collapsible ? (
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full items-center px-3 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-[0.14em] transition-opacity duration-200"
      style={{
        color: "var(--sidebar-text-color, #ffffff)",
        opacity: labelsVisible ? 0.55 : 0,
      }}
      aria-expanded={open}
    >
      <span className="flex-1 text-left">{label}</span>
      {badge != null && badge > 0 && (
        <span
          className="mr-1 rounded-full px-1.5 py-0.5 text-[10px] font-semibold tabular-nums"
          style={{ backgroundColor: hairline(22) }}
        >
          {badge}
        </span>
      )}
      <ChevronDown className={cn("h-3 w-3 flex-shrink-0 transition-transform", open && "rotate-180")} />
    </button>
  ) : (
    <p
      className="px-3 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-[0.14em] transition-opacity duration-200"
      style={{
        color: "var(--sidebar-text-color, #ffffff)",
        // 0.55 is the resting weight for a group heading; the fade runs to that,
        // not to full strength.
        opacity: labelsVisible ? 0.55 : 0,
      }}
    >
      {label}
    </p>
  );

  // Children stay mounted when the group closes so a programmatic navigate
  // still works. Height slide is grid-template-rows 0fr/1fr, not a max-height
  // hack. Icon rail (`collapsed`) skips the slide and always shows children.
  const body = collapsible ? (
    <div
      className="grid"
      style={{
        gridTemplateRows: open ? "1fr" : "0fr",
        transitionProperty: "grid-template-rows",
        transitionDuration: `${slideMs}ms`,
        transitionTimingFunction: "ease-out",
      }}
    >
      <div className="min-h-0 overflow-hidden">
        <div className="space-y-1">{children}</div>
      </div>
    </div>
  ) : (
    <div className="space-y-1">{children}</div>
  );

  return (
    <div>
      {heading}
      {body}
    </div>
  );
}

/**
 * Caption for a cluster inside an expandable row's children. Lighter than a
 * top-level NavGroup label so the two levels stay distinguishable.
 */
function NavSubGroup({
  label,
  labelsVisible = true,
}: {
  label: string;
  labelsVisible?: boolean;
}) {
  return (
    <p
      className="px-2 pb-0.5 pt-2 text-[9px] font-semibold uppercase tracking-[0.13em] transition-opacity duration-200"
      style={{
        color: "var(--sidebar-text-color, #ffffff)",
        opacity: labelsVisible ? 0.42 : 0,
      }}
    >
      {label}
    </p>
  );
}

/** Indent rail for the children of an expandable row. */
function NavSub({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="ml-5 space-y-0.5 border-l pl-3"
      style={{ borderColor: hairline(22) }}
    >
      {children}
    </div>
  );
}

type SidebarProps = {
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  onOpenAddUser?: () => void;
};

export function Sidebar({ collapsed = false, onToggleCollapse, onOpenAddUser }: SidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();

  // SD-8.1 / FR-29: while the gate resolves, the entry stays HIDDEN — an entry that appears and
  // then vanishes has already advertised the pre-release feature (the SD-2.1 rule).
  const { hasAccess: designFeatureAccess, loading: designAccessLoading } = useDesignFeatureAccess();
  const showDesignSettings = !designAccessLoading && designFeatureAccess;

  // No need for manual cleanup - let CSS classes handle everything
  const { user } = useAuth();
  const {
    hasPermission,
    hasAnyPermission,
    permissions,
    roleInfo,
    loading: permissionsLoading,
  } = usePermissions();

  // KNK-1.11. Same rule as the panel itself (knockModeSettingsVisible), so the entry and the
  // page cannot disagree; hidden while either read is in flight.
  const { status: knockStatus, loading: knockLoading } = useKnockMode();
  const showKnockSettings = knockModeSettingsVisible({
    status: knockStatus,
    loading: knockLoading || permissionsLoading,
    canManageField: hasAnyPermission(['field.settings.manage', 'system.override']),
  });
  // KNK-1.55. The panel renders only for a superadmin; so does its entry.
  const showModulesSettings = !permissionsLoading && hasPermission('system.override');
  // RBAC-6.1 / FR-19. Hidden while either read is in flight, like Design's entry.
  const { isOn: accountHasModule, loading: accountModulesLoading } = useAccountFeatures();
  const showContractsSettings =
    !permissionsLoading &&
    !accountModulesLoading &&
    featureVisibleTo({ accountEnabled: accountHasModule('contracts'), isSuperadmin: hasPermission('system.override') });
  // KNK-1.14. The same rule DoorMap applies to itself, so the entry and the page agree.
  const showDoorMap = doorMapVisible({
    status: knockStatus,
    loading: knockLoading || permissionsLoading,
    canView: hasAnyPermission(['door.view', 'system.override']),
    canSeed: hasAnyPermission(['territory.manage', 'system.override']),
  });
  // KNK-1.57. The same rule LiveTeamMap applies to itself. Decided from permissions, never by
  // calling live_team_map(), which logs a view every time it is called.
  const showLiveTeamMap = liveTeamMapVisible({
    status: knockStatus,
    loading: knockLoading || permissionsLoading,
    canView: hasAnyPermission([...LIVE_MAP_PERMISSIONS]),
  });
  // KNK-1.44. The same rule KnockReviews applies to itself.
  const showKnockReviews = knockReviewVisible({
    status: knockStatus,
    loading: knockLoading || permissionsLoading,
    canView: hasAnyPermission([...KNOCK_REVIEW_PERMISSIONS]),
  });

  // COM-3.4. The account rollout gate. `loading` is deliberately treated as OFF
  // rather than as a spinner in the nav; see the earnings entry below.
  const { enabled: commissionEnabled } = useCommissionCostingEnabled();
  const commissionNavViewer = useCommissionNavViewer();
  const commissionAreaCounts = useCommissionAreaCounts();

  const showCommissionGroup = canSeeCommissionGroup(commissionNavViewer);
  const { hasAccess: hasOnboardingAccess } = useOnboardingAccess();
  const [teamId, setTeamId] = useState<number | null>(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [commercialPipelines, setCommercialPipelines] = useState<PipelineNavEntry[]>([]);
  const [residentialPipelines, setResidentialPipelines] = useState<PipelineNavEntry[]>([]);
  const [internalPipelines, setInternalPipelines] = useState<PipelineNavEntry[]>([]);
  const [isCommercialOpen, setIsCommercialOpen] = useState(false);
  const [isResidentialOpen, setIsResidentialOpen] = useState(false);
  const [isInternalOpen, setIsInternalOpen] = useState(false);

  const [navGroups, setNavGroups] = useState<SidebarNavGroupMap>(() => loadSidebarNavGroups());

  /**
   * SUB-4.2 · FR34 for the All Projects row, SUB-4.5 · FR40 for the navigator.
   * Read once here because the pipeline fetch below depends on it.
   */
  // `currentTenant` is here for SUB-5.9's dashboard memory: it names the account
  // whose dashboards `getDashboards` will list, which is what the memory must be
  // keyed by.
  const { mergedScope, currentTenant } = useTenant();
  const scopeReach = scopeReachKey(mergedScope);
  /**
   * Whose copy of a workflow labels the navigator entry. Held separately from
   * `scopeReach` because that key deliberately does not carry the anchor: it
   * answers "did the REACH move", and the anchor can be filled in on a later
   * status read without the set of accounts changing.
   */
  const groupingAnchor =
    mergedScope.anchorTenantId ?? mergedScope.singleAccountTenantId ?? null;

  // --- peek-expand ---------------------------------------------------------
  // Collapsed, the rail opens to its full self on hover and closes again on
  // leave, so collapsed and expanded aren't two separate designs.
  const [peeking, setPeeking] = useState(false);
  const peekTimer = React.useRef<number | undefined>(undefined);
  // Hover-to-open is meaningless on touch, where every tap would read as a hover
  // and strand the rail open. Pointer capability is checked once on mount.
  const canPeek = React.useRef(false);

  useEffect(() => {
    canPeek.current =
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(hover: hover) and (pointer: fine)").matches;
  }, []);

  // Never leave a peek stranded when the rail is pinned open from the toggle.
  useEffect(() => {
    if (!collapsed) setPeeking(false);
  }, [collapsed]);

  useEffect(() => () => window.clearTimeout(peekTimer.current), []);

  const openPeek = () => {
    if (!collapsed || !canPeek.current) return;
    window.clearTimeout(peekTimer.current);
    // Delay so crossing the rail on the way somewhere else doesn't open it.
    peekTimer.current = window.setTimeout(() => setPeeking(true), 260);
  };

  const closePeek = () => {
    window.clearTimeout(peekTimer.current);
    // Shorter grace period so a wobble near the edge doesn't slam it shut.
    peekTimer.current = window.setTimeout(() => setPeeking(false), 140);
  };

  /** Where the rail is heading. The toggle still governs its resting width. */
  const expandTarget = !collapsed || peeking;

  /**
   * Opening and closing are sequenced so the words and the width don't fight.
   *
   * `labelsVisible` lags the opening by PEEK_REVEAL_MS so the rail is already
   * widening before the labels start to arrive — otherwise they are fully legible
   * inside a 56px rail and it reads as the text appearing first with the rail
   * catching up. `showExpanded` lags the *closing* by the same fade duration, so
   * the labels get to fade out instead of vanishing a beat before the rail shrinks.
   */
  const PEEK_REVEAL_MS = 120;
  const LABEL_FADE_MS = 200;

  const [labelsVisible, setLabelsVisible] = useState(!collapsed);
  const [showExpanded, setShowExpanded] = useState(!collapsed);

  const toggleNavGroup = (id: SidebarNavGroupId) => {
    // Icon-only / peek rail must not overwrite the cookie.
    if (!showExpanded) return;
    setNavGroups((prev) => {
      const next: SidebarNavGroupMap = {
        ...prev,
        [id]: !isSidebarNavGroupOpen(prev, id),
      };
      writeSidebarNavGroups(next);
      return next;
    });
  };

  const groupOpen = (id: SidebarNavGroupId) =>
    paintedSidebarNavGroupOpen(navGroups, id, location.pathname);

  useEffect(() => {
    if (expandTarget) {
      setShowExpanded(true);
      // Pinned open from the toggle: no peek animation to sequence against.
      if (!collapsed) {
        setLabelsVisible(true);
        return;
      }
      const t = window.setTimeout(() => setLabelsVisible(true), PEEK_REVEAL_MS);
      return () => window.clearTimeout(t);
    }

    setLabelsVisible(false);
    const t = window.setTimeout(() => setShowExpanded(false), LABEL_FADE_MS);
    return () => window.clearTimeout(t);
  }, [expandTarget, collapsed]);

  // Fetch the user's team ID
  useEffect(() => {
    if (user) {
      const fetchTeamId = async () => {
        const { data, error } = await supabase
          .from('user_teams')
          .select('team_id')
          .eq('user_id', user.id)
          .limit(1);
        if (error) {
          // Don't log this error as it's expected when user doesn't have a team assigned
          setTeamId(null);
        } else if (!data || data.length === 0) {
          // User doesn't have a team assigned - this is normal
          setTeamId(null);
        } else {
          setTeamId(data[0].team_id);
        }
      };
      fetchTeamId();
    } else {
      setTeamId(null);
    }
  }, [user]);

  // Fetch pipelines for submenu navigation with permission filtering
  //
  // SUB-4.5 · FR40. The list now comes from `merged_scope_pipelines()` rather
  // than a tenant-pinned table read. That RPC answers identically for every
  // caller who is not in merged scope, so this is one path rather than two, and
  // it narrows to the caller's own account if it cannot be used at all.
  useEffect(() => {
    const fetchPipelines = async () => {
      if (!user) return;

      try {
        const { rows } = await fetchScopePipelines();

        // The RPC hands back the whole `pipelines` row. Narrow it here to the
        // fields the navigator and the permission filter actually read, so a
        // column added to the table later cannot change this component by
        // accident, and so the permission arrays are arrays before
        // `filterPipelinesByAccess` indexes into them.
        const asStrings = (value: unknown): string[] | undefined =>
          Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : undefined;

        const data: Pipeline[] = rows.map((row) => ({
          id: row.id,
          name: row.name ?? '',
          pipeline_type: (row.pipeline_type ?? '') as Pipeline['pipeline_type'],
          role_permissions: asStrings(row.role_permissions),
          user_permissions: asStrings(row.user_permissions),
          team_permissions: asStrings(row.team_permissions),
          tenant_id: row.tenant_id ?? null,
          cloned_from_pipeline_id:
            typeof row.cloned_from_pipeline_id === 'number' ? row.cloned_from_pipeline_id : null,
        }));

        // Get user role information
        const { data: userData, error: userError } = await supabase
          .from('users')
          .select(`
            id,
            role_id,
            roles (
              id,
              name
            )
          `)
          .eq('id', user.id)
          .single();

        if (userError) {
          console.error('Error fetching user role:', userError);
          return;
        }

        // Extract role name
        const roleData = Array.isArray(userData.roles) ? userData.roles[0] : userData.roles;
        const userRole = roleData?.name || '';

        // Check for system override permissions
        const hasSystemOverride = hasPermission('system.override') ||
                                 hasPermission('tenant.manage') ||
                                 userRole === 'Superadmin';

        // Create user info object for permission checking
        const userInfo = {
          id: user.id,
          role: userRole,
          teamId: teamId?.toString()
        };

        // Filter pipelines by type and apply permission filtering
        const allCommercial = data?.filter(p => p.pipeline_type === 'commercial') || [];
        const allResidential = data?.filter(p => p.pipeline_type === 'residential') || [];
        const allInternal = data?.filter(p => p.pipeline_type === 'onboarding') || [];

        // Apply permission filtering - only show pipelines with general permissions OR specific access
        const filteredCommercial = allCommercial.filter(pipeline => {
          // If user has general commercial pipeline permission, apply access filtering
          if (hasGeneralPipelinePermission('commercial', permissions)) {
            return filterPipelinesByAccess([pipeline], userInfo, hasSystemOverride).length > 0;
          }
          return false;
        });

        const filteredResidential = allResidential.filter(pipeline => {
          if (hasGeneralPipelinePermission('residential', permissions)) {
            return filterPipelinesByAccess([pipeline], userInfo, hasSystemOverride).length > 0;
          }
          return false;
        });

        const filteredInternal = allInternal.filter(pipeline => {
          if (hasGeneralPipelinePermission('onboarding', permissions)) {
            return filterPipelinesByAccess([pipeline], userInfo, hasSystemOverride).length > 0;
          }
          return false;
        });

        // SUB-4.5 · AC1, AC2. One entry per workflow, not one per account.
        //
        // ⚠️ GROUPED AFTER THE PERMISSION FILTER, NOT BEFORE. A group whose
        // anchor copy the caller may not see should still be reachable through
        // the client copy they may, and grouping first would have made one
        // inaccessible member hide every accessible one behind it.
        //
        // ⚠️ Grouping runs per type, which is also what the group key encodes.
        // Two accounts that independently built a "Standard" pipeline, one
        // commercial and one residential, are two workflows with one name.
        setCommercialPipelines(groupPipelinesByLineage(filteredCommercial, groupingAnchor));
        setResidentialPipelines(groupPipelinesByLineage(filteredResidential, groupingAnchor));
        setInternalPipelines(groupPipelinesByLineage(filteredInternal, groupingAnchor));

      } catch (error) {
        console.error('Error fetching pipelines:', error);
      }
    };

    fetchPipelines();
    // SUB-4.5. `scopeReach` is in the deps because entering or leaving merged
    // scope changes WHICH pipelines exist for this caller, and nothing else in
    // this list moves when it does. It is `scopeReachKey`, not `mergedScopeKey`:
    // the navigator must refetch when the reach moves, not when the picker gains
    // a row it is not rendering.
  }, [user, teamId, permissions, hasPermission, scopeReach, groupingAnchor]);

  // Auto-expand relevant submenu based on current path
  useEffect(() => {
    if (location.pathname === "/app/pipeline") {
      setIsCommercialOpen(true);
    } else if (location.pathname === "/app/pipeline-resi") {
      setIsResidentialOpen(true);
    } else if (location.pathname === "/app/onboarding") {
      setIsInternalOpen(true);
    }
  }, [location.pathname]);

  // Check if user can add team users (Team Owner, Team Admin, or full admin)
  // Only show Add User for Team Owners and Team Admins (full admins use settings menu)
  const canAddTeamUsers = roleInfo?.name === 'Team Owner' || roleInfo?.name === 'Team Admin';

  // Main navigation items defined inside the component to access teamId
  const mainNavItems: NavItem[] = [
    { title: "Dashboard", href: "/app/dashboard", icon: <LayoutDashboard className="h-5 w-5" />, isVisible: false },
    { title: "Dashboards", href: "/app/dashboards", icon: <LayoutDashboard className="h-5 w-5" /> },
    ...(teamId === 163
      ? [{
          title: "Aurora",
          href: "/app/aurora", // Internal route for Aurora iframe
          icon: <img src="/tets.png" className="h-5 w-5 min-w-[20px] min-h-[20px] object-contain" alt="Aurora icon" />
        }]
      : []),
    { title: "Chat", href: "/app/chat", icon: <MessageSquare className="h-5 w-5" /> },
    // KNK-1.14 / FR-12. Absent, not disabled, while Knock Mode is off (FR-11).
    ...(showDoorMap
      ? [{ title: "Door map", href: "/app/doors", icon: <MapPinned className="h-5 w-5" /> }]
      : []),
    // KNK-1.57 / FR-33a. Absent while Knock Mode is off or without a live view rung.
    ...(showLiveTeamMap
      ? [{ title: "Live team map", href: "/app/live-team", icon: <Radio className="h-5 w-5" /> }]
      : []),
    // KNK-1.44 / FR-35. Absent while Knock Mode is off or without a review rung.
    ...(showKnockReviews
      ? [{ title: "Flag review", href: "/app/knock-reviews", icon: <ShieldCheck className="h-5 w-5" /> }]
      : []),
    // COM-3.4. A rep's own earnings.
    //
    // ⚠️ GATED ON THE ACCOUNT'S ROLLOUT SWITCH AND ON NOTHING ELSE, WHICH IS THE
    // POINT OF THE STORY. It needs no admin-granted report permission: the RPCs
    // behind it are scoped to auth.uid() in the database, so every user of an
    // enabled account sees their own numbers and nobody else's. Gating this entry
    // on view_finance would put a rep's own pay behind a finance permission,
    // which is exactly the failure the epic exists to end.
    //
    // ⚠️ ABSENT, NOT DISABLED, AND ABSENT WHILE THE GATE IS STILL LOADING. LDG-1.1's
    // AC is a silent absence; an entry that appears and then vanishes has already
    // told the account the feature exists.
    ...(commissionEnabled
      ? [{ title: "My earnings", href: "/app/earnings", icon: <Coins className="h-5 w-5" /> }]
      : []),
    // Temporarily hidden - knowledgebase is outdated
    // { title: "Knowledge Base", href: "/app/faq", icon: <BookOpen className="h-5 w-5" /> }
  ];

  const isActive = (path: string) => {
    // Handle settings tabs with query parameters
    if (path.includes('?tab=') && location.pathname === '/app/settings' && location.search.includes(path.split('?tab=')[1])) {
      return true;
    }

    // Handle pipeline URLs with query parameters
    if (path.includes('?pipelineId=')) {
      const basePath = path.split('?')[0];
      return location.pathname === basePath;
    }

    // Default path matching
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  /**
   * The sidebar is position:fixed, so MainLayout offsets the page with a margin.
   * That margin used to be the hardcoded `md:ml-56` twin of a fixed `w-56`; now
   * that the rail sizes to its content, it publishes its measured width and the
   * layout follows it. Without this the nav would simply overlap the page.
   */
  const asideRef = React.useRef<HTMLElement | null>(null);

  useEffect(() => {
    const el = asideRef.current;
    if (!el) return;

    // A peek overlays the page rather than pushing it, so while the rail is
    // collapsed the published width is its *resting* 3.5rem no matter how wide it
    // currently is. Keying this off the measured width instead would shove the
    // page open on hover, and — because the rail stays wide for the label fade —
    // would still be reporting 196px for 200ms after the peek had ended.
    const publish = () =>
      document.documentElement.style.setProperty(
        "--app-sidebar-w",
        collapsed ? "3.5rem" : `${Math.round(el.getBoundingClientRect().width)}px`
      );

    publish();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(publish);
    ro.observe(el);
    return () => ro.disconnect();
  }, [collapsed]);

  const toggleSettingsSubmenu = () => {
    if (showExpanded) {
      setIsSettingsOpen(!isSettingsOpen);
    }
  };

  const toggleCommercialSubmenu = () => {
    if (showExpanded) {
      setIsCommercialOpen(!isCommercialOpen);
    }
  };

  const toggleResidentialSubmenu = () => {
    if (showExpanded) {
      setIsResidentialOpen(!isResidentialOpen);
    }
  };

  const toggleInternalSubmenu = () => {
    if (showExpanded) {
      setIsInternalOpen(!isInternalOpen);
    }
  };

  /**
   * Navigate straight to the last-viewed dashboard, falling back to the default.
   *
   * ⚠️ SUB-5.9 · F-98. The memory is read THROUGH ITS SCOPE. It used to be a
   * bare uuid that was never cleared, so after an account switch this navigated
   * to a dashboard belonging to the account just left, RLS refused it, and the
   * user met "Dashboard not found", which reads as a permissions failure and is
   * not one. A memory recorded in another account or another scope mode now
   * simply does not match, and the fallback below runs instead.
   *
   * ⚠️ An unresolved scope yields a null key, which reads as "no memory" and
   * takes the fallback. That is the safe direction: resolving the default is
   * always correct, following a guess is what caused F-98.
   */
  const goToDashboards = async () => {
    try {
      const lastViewedId = readLastViewedDashboard(
        localStorage.getItem(LAST_VIEWED_DASHBOARD_KEY),
        dashboardScopeKey(mergedScope, currentTenant?.tenant_id ?? null),
      );

      /**
       * D89. ⚠️ **BEFORE THE REMEMBERED DASHBOARD, AND SUB-5.10 PUT IT AFTER.**
       * That ordering was right while merged scope still offered other
       * dashboards to choose between. It no longer does: D89 leaves exactly one
       * entry in the picker and redirects every other id to it, so a remembered
       * id here could only ever navigate to something that immediately
       * redirects, and the user would watch it happen.
       *
       * ⚠️ Null outside merged scope, so single-account behaviour is untouched
       * and the remembered dashboard still wins there.
       */
      const scopeDefault = defaultDashboardIdForScope(mergedScope);
      if (scopeDefault) {
        navigate(`/app/dashboards/${scopeDefault}`);
        return;
      }

      if (lastViewedId) {
        navigate(`/app/dashboards/${lastViewedId}`);
        return;
      }

      /**
       * SUB-5.10 · FR44. In merged scope the All Accounts overview is where this
       * lands, so an agency gets a useful screen without configuring anything.
       *
       */

      const { data: dashboards } = await supabase
        .from('dashboards')
        .select('*')
        .order('created_at', { ascending: false });

      const defaultDashboard = dashboards?.find(
        (d: { is_default?: boolean }) => d.is_default
      );
      if (defaultDashboard) {
        navigate(`/app/dashboards/${defaultDashboard.id}`);
      } else if (dashboards && dashboards.length > 0) {
        navigate(`/app/dashboards/${dashboards[0].id}`);
      } else {
        console.warn('No dashboards found for user');
        navigate('/app/dashboard');
      }
    } catch (error) {
      console.error('Failed to navigate to dashboard:', error);
      navigate('/app/dashboard');
    }
  };

  const pipelineGroups = [
    {
      key: 'commercial',
      title: 'Commercial',
      icon: <Building2 className="h-5 w-5" />,
      childIcon: <Building2 className="h-3 w-3" />,
      basePath: '/app/pipeline',
      pipelines: commercialPipelines,
      open: isCommercialOpen,
      toggle: toggleCommercialSubmenu,
      visible: true,
    },
    {
      key: 'residential',
      title: 'Residential',
      icon: <Home className="h-5 w-5" />,
      childIcon: <Home className="h-3 w-3" />,
      basePath: '/app/pipeline-resi',
      pipelines: residentialPipelines,
      open: isResidentialOpen,
      toggle: toggleResidentialSubmenu,
      visible: true,
    },
    {
      key: 'internal',
      title: 'Internal',
      icon: <UsersIcon className="h-5 w-5" />,
      childIcon: <UsersIcon className="h-3 w-3" />,
      basePath: '/app/onboarding',
      pipelines: internalPipelines,
      open: isInternalOpen,
      toggle: toggleInternalSubmenu,
      visible: hasOnboardingAccess,
    },
    // A group whose pipelines were all filtered out by permissions has nothing to
    // show, so hide the row rather than offering an accordion that opens onto
    // nothing.
  ].filter((g) => g.visible && g.pipelines.length > 0);

  /**
   * Group visibility is resolved here rather than by wrapping each group in
   * <ConditionalRender>, for two reasons: a guard around the children still
   * leaves the group's label rendered above an empty list, and PermissionGuard's
   * loading state is a `min-h-screen` spinner — which, inside a sidebar, is a
   * full-height spinner in the nav. These read the same permission set the guard
   * would (useHasPermission is `usePermissions().hasPermission`).
   */
  const canSeeRecords = !permissionsLoading && hasPermission("reporting.admin");
  const canSeeSettings =
    !permissionsLoading && hasAnyPermission(["manage_settings", "system.override"]);
  /**
   * RBAC-3.1. roles.administer opens Settings → Roles & Permissions on its own. A holder without
   * manage_settings (a duplicated Admin, once RBAC-3.3 strips reserved grants) sees the Settings
   * row with that one entry under it, and nothing that SettingsGuard would bounce.
   */
  const canSeeRolesSettings = !permissionsLoading && canAdministerRoles(permissions);
  const settingsNav = { canSeeSettings, canAdministerRoles: canSeeRolesSettings };
  const showSettingsRow = canSeeSettings || canSeeRolesSettings;
  /**
   * SUB-2.11 · FR63. The nav row is a convenience; `/app/sub-accounts` guards
   * itself with the same hook, so a hand-typed URL is refused whether or not this
   * row rendered. That separation is the SUB-2.2 lesson: hiding an entry does not
   * make a page not exist.
   */
  const { canSeeEntry: canSeeSubAccounts } = useSubAccountUsage();
  const showAdminGroup = canAddTeamUsers || showSettingsRow || canSeeSubAccounts;

  return (
    <aside
      ref={asideRef}
      onMouseEnter={openPeek}
      onMouseLeave={closePeek}
      onFocusCapture={() => collapsed && setPeeking(true)}
      onBlurCapture={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) closePeek();
      }}
      className={cn(
        "h-full flex-shrink-0 overflow-hidden bg-zinc-900 z-[200]",
        "transition-[min-width,max-width,box-shadow] duration-200 ease-out",
        peeking && "shadow-2xl"
      )}
      style={{
        backgroundColor: 'var(--sidebar-color, #000000)',
        // width:max-content wants the content's width; the two clamps below are
        // what actually animate, which is why the peek slides rather than snaps —
        // `width:auto` is not interpolable, min/max-width are. Expanded, the rail
        // sizes to its longest label, bounded so one long pipeline name can't take
        // over the page.
        width: 'max-content',
        minWidth: showExpanded ? '14rem' : '3.5rem',
        maxWidth: showExpanded ? '20rem' : '3.5rem',
      }}
    >
      <div className="flex flex-col h-full relative">
        {/* Navigation */}
        <div className="p-4 flex-grow overflow-y-auto scrollbar-thin scrollbar-track-transparent scrollbar-thumb-gray-600 hover:scrollbar-thumb-gray-500">
          <nav className="pb-16">
            {/* Tenant Dropdown - appears above the groups */}
            <TenantDropdown collapsed={!showExpanded} />

            <NavGroup
              label="Workspace"
              collapsed={!showExpanded}
              labelsVisible={labelsVisible}
              collapsible={showExpanded}
              open={groupOpen("workspace")}
              onToggle={() => toggleNavGroup("workspace")}
            >
              {mainNavItems.map((item) =>
                item.isVisible === undefined || item.isVisible ? (
                  item.title === "Dashboards" ? (
                    <NavRow
                      key={item.href}
                      title={item.title}
                      icon={item.icon}
                      collapsed={!showExpanded}
                      labelsVisible={labelsVisible}
                      active={isActive(item.href)}
                      onClick={goToDashboards}
                    />
                  ) : (
                    <NavRow
                      key={item.href}
                      title={item.title}
                      icon={item.icon}
                      collapsed={!showExpanded}
                      labelsVisible={labelsVisible}
                      active={isActive(item.href)}
                      to={item.href}
                    />
                  )
                ) : null
              )}

              {/*
                SUB-5.23. ⚠️ **THE "All Projects" ROW AND ITS PAGE ARE DELETED.**
                SUB-4.2 built it as the merged list's home and SUB-4.3 gave it
                the portable filter panel. D87 then moved every drill-down into
                the slide panel, which left the page's only remaining job as free
                browsing that nobody was doing. ⚠️ `/app/projects/:projectId`
                still exists and is untouched: it is the canonical project deep
                link, built into automation emails and share URLs.
              */}
            </NavGroup>

            {pipelineGroups.length > 0 && (
            <NavGroup
              label="Pipelines"
              collapsed={!showExpanded}
              labelsVisible={labelsVisible}
              collapsible={showExpanded}
              open={groupOpen("pipelines")}
              onToggle={() => toggleNavGroup("pipelines")}
            >
              {pipelineGroups.map((group) => (
                <div key={group.key}>
                  <NavRow
                    title={group.title}
                    icon={group.icon}
                    collapsed={!showExpanded}
                    labelsVisible={labelsVisible}
                    active={location.pathname === group.basePath}
                    onClick={group.toggle}
                    expandable
                    expanded={group.open}
                  />
                  {showExpanded && group.open && (
                    <NavSub>
                      {/* SUB-4.5 · AC1. One row per workflow. The row links to
                          the representative copy; the board it opens builds an
                          account tab strip from the same grouping rule, so
                          every member is one click away and none of them gets a
                          duplicate row here. A row stays active while ANY member
                          is the open pipeline, which is what makes switching
                          tabs read as staying inside one navigator entry. */}
                      {group.pipelines.map((entry) => (
                        <NavRow
                          key={entry.key}
                          title={entry.label}
                          icon={group.childIcon}
                          nested
                          labelsVisible={labelsVisible}
                          to={`${group.basePath}?pipelineId=${entry.representative.id}`}
                          active={
                            location.pathname === group.basePath &&
                            entry.members.some((member) =>
                              new URLSearchParams(location.search).get('pipelineId') ===
                              String(member.id)
                            )
                          }
                        />
                      ))}
                    </NavSub>
                  )}
                </div>
              ))}
            </NavGroup>
            )}

            {showCommissionGroup && (
              <NavGroup
                label={COMMISSION_AREA_COPY.groupLabel}
                collapsed={!showExpanded}
                labelsVisible={labelsVisible}
                collapsible={showExpanded}
                open={groupOpen("commission")}
                onToggle={() => toggleNavGroup("commission")}
                badge={
                  showExpanded && !groupOpen("commission")
                    ? rolledBadgeCount(commissionAreaCounts)
                    : null
                }
              >
                {visibleCommissionDestinations(commissionNavViewer).map((item) => {
                  const active =
                    item.id === "overview"
                      ? isCommissionOverviewPath(location.pathname)
                      : item.id === "setup"
                        ? isCommissionSetupPath(location.pathname)
                        : isActive(item.href);
                  return (
                    <NavRow
                      key={item.href}
                      title={item.title}
                      icon={COMMISSION_DESTINATION_ICONS[item.id]}
                      collapsed={!showExpanded}
                      labelsVisible={labelsVisible}
                      active={active}
                      to={item.href}
                      badge={badgeForDestination(item.id, commissionAreaCounts)}
                    />
                  );
                })}
              </NavGroup>
            )}

            {canSeeRecords && (
              <NavGroup
                label="Records"
                collapsed={!showExpanded}
                labelsVisible={labelsVisible}
                collapsible={showExpanded}
                open={groupOpen("records")}
                onToggle={() => toggleNavGroup("records")}
              >
                {adminNavItems
                  .filter((item) => item.href !== CONTRACTS_HREF || showContractsSettings)
                  .map((item) => (
                  <NavRow
                    key={item.href}
                    title={item.title}
                    icon={item.icon}
                    collapsed={!showExpanded}
                    labelsVisible={labelsVisible}
                    active={isActive(item.href)}
                    to={item.href}
                  />
                ))}
              </NavGroup>
            )}

            {showAdminGroup && (
              <NavGroup
                label="Admin"
                collapsed={!showExpanded}
                labelsVisible={labelsVisible}
                collapsible={showExpanded}
                open={groupOpen("admin")}
                onToggle={() => toggleNavGroup("admin")}
              >
                {canAddTeamUsers && (
                  <NavRow
                    title="Add User"
                    icon={<UserPlus className="h-5 w-5" />}
                    collapsed={!showExpanded}
                    labelsVisible={labelsVisible}
                    onClick={onOpenAddUser}
                  />
                )}

                {/* SUB-5.10 · FR44. ⚠️ THE `Portfolio` NAV ROW WAS REMOVED HERE,
                    DELIBERATELY, AND THE PAGE WAS NOT.
                    SUB-5.1 put the roll-up behind its own top-level row because
                    the Dashboards surface could not host it. SUB-5.10 made it the
                    All Accounts overview inside Dashboards, which is where people
                    look for a dashboard, so the row became a second door onto one
                    room and the sidebar carried a surface the product was trying
                    to reduce, not add to.
                    `/app/portfolio` still resolves, so existing bookmarks and any
                    link written before today keep working. Nothing was deleted
                    except the row. */}

                {/* SUB-2.11 · FR63. Present for a parent with zero sub-accounts,
                    which is exactly when the account picker above is hidden. */}
                {canSeeSubAccounts && (
                  <NavRow
                    title="Sub-accounts"
                    icon={<Network className="h-5 w-5" />}
                    collapsed={!showExpanded}
                    labelsVisible={labelsVisible}
                    active={isActive("/app/sub-accounts")}
                    to="/app/sub-accounts"
                  />
                )}

                {showSettingsRow && (
                  <div>
                    <NavRow
                      title="Settings"
                      icon={<Settings className="h-5 w-5" />}
                      collapsed={!showExpanded}
                      labelsVisible={labelsVisible}
                      active={isActive("/app/settings")}
                      onClick={toggleSettingsSubmenu}
                      expandable
                      expanded={isSettingsOpen}
                    />
                    {showExpanded && isSettingsOpen && (
                      <NavSub>
                        {settingsGroups
                          .map((group) => ({
                            ...group,
                            items: group.items
                              .filter((item) => settingsEntryVisible(item.href, settingsNav))
                              .filter((item) => item.href !== DESIGN_SETTINGS_HREF || showDesignSettings)
                              .filter((item) => item.href !== CONTRACTS_SETTINGS_HREF || showContractsSettings)
                              .filter((item) => item.href !== KNOCK_SETTINGS_HREF || showKnockSettings)
                              .filter((item) => item.href !== MODULES_SETTINGS_HREF || showModulesSettings),
                          }))
                          // A group with nothing visible would leave its caption over an empty list.
                          .filter((group) => group.items.length > 0)
                          .map((group) => (
                          <div key={group.label}>
                            <NavSubGroup
                              label={group.label}
                              labelsVisible={labelsVisible}
                            />
                            {group.items
                              .map((item) => (
                              <NavRow
                                key={item.href}
                                title={item.title}
                                icon={item.icon}
                                nested
                                to={item.href}
                                active={isActive(item.href)}
                                labelsVisible={labelsVisible}
                              />
                            ))}
                          </div>
                        ))}
                      </NavSub>
                    )}
                  </div>
                )}
              </NavGroup>
            )}
          </nav>
        </div>

        {/*
          Pin control. Expanded is the locked state: the rail stays open and never
          peeks. Collapsed is the icon rail with hover-to-peek. The choice persists
          via SidebarContext, so someone who doesn't want the collapsed version sets
          it once. Labels name the outcome rather than the mechanism — "keep open"
          rather than "expand" — because with peeking, expanding already happens on
          its own and the button is specifically about making it stay.
        */}
        <button
          onClick={onToggleCollapse}
          className={cn(
            "absolute bottom-3 right-3 rounded-full p-1.5 transition-colors",
            "hover:bg-[var(--sidebar-hover-bg-color,#374151)]",
            "focus-visible:outline-none focus-visible:ring-2",
            "focus-visible:ring-[var(--sidebar-hover-text-color,#ffffff)]"
          )}
          style={{
            backgroundColor: 'var(--sidebar-hover-bg-color, #374151)',
            color: collapsed
              ? 'var(--sidebar-text-color, #ffffff)'
              : 'var(--sidebar-hover-text-color, #ffffff)',
          }}
          title={collapsed ? "Keep sidebar open" : "Collapse to icon rail"}
          aria-label={collapsed ? "Keep sidebar open" : "Collapse to icon rail"}
        >
          {collapsed ? (
            <PanelLeftOpen className="h-4 w-4" />
          ) : (
            <PanelLeftClose className="h-4 w-4" />
          )}
        </button>
      </div>

    </aside>
  );
}
